import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { useSnackPackContext } from "@app.automotus.io/components/context/SnackPack";
import {
  ASSIGN_ENFORCEMENT_INTENT_OPERATOR,
  AssignEnforcementIntentOperatorData,
  AssignEnforcementIntentOperatorVars,
  EnforcementIntentDetail,
} from "common/graphql";
import { AssignableOperator } from "common/graphql/queries/GetAssignableEnforcementIntentOperators";
import OperatorSelect from "./OperatorSelect";

export interface OperatorAssignmentItemProps {
  intent: EnforcementIntentDetail;
  refetch: () => void;
}

/**
 * Post-review operator assignment (AUT-5033). Renders in the Notice Details
 * grid on issued unpermitted-vehicle citations: read-only operator name once
 * assigned, otherwise a searchable dropdown with an explicit confirm —
 * assignment is one-time and bills the operator, so there is no undo.
 */
export const OperatorAssignmentItem: React.FC<OperatorAssignmentItemProps> = ({ intent, refetch }) => {
  const [selectedOperator, setSelectedOperator] = useState<AssignableOperator | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const { publishSnackbarMessage } = useSnackPackContext();

  const [assignOperator, { loading: isAssigning }] = useMutation<
    AssignEnforcementIntentOperatorData,
    AssignEnforcementIntentOperatorVars
  >(ASSIGN_ENFORCEMENT_INTENT_OPERATOR);

  const handleAssign = () => {
    if (!selectedOperator) {
      return;
    }
    assignOperator({
      variables: { intentId: intent.id, operatorAccountId: selectedOperator.accountId },
      onError(err) {
        console.error(err);
        setIsConfirmOpen(false);
        // Prefer the structured code the action forwards from the API
        // (ServiceError.Code === "Conflict" for the one-time violation); the
        // message regex is a fallback so a wording change degrades to the
        // generic toast rather than mis-detecting (Greptile, #1274).
        const conflictCode = err.graphQLErrors?.some(
          (gqlErr) =>
            (gqlErr.extensions?.code ?? (gqlErr.extensions?.internal as { code?: string })?.code) === "Conflict",
        );
        const isConflict = conflictCode || /one-time|already assigned/i.test(err.message);
        publishSnackbarMessage({
          message: isConflict ? "An operator is already assigned to this citation" : "Failed to assign operator",
          severity: "error",
        });
        if (isConflict) {
          // Someone else assigned first — refresh to show who.
          refetch();
        }
      },
      onCompleted() {
        setIsConfirmOpen(false);
        publishSnackbarMessage({
          message: `Assigned to ${selectedOperator.organizationName}`,
          severity: "success",
        });
        refetch();
      },
    });
  };

  return (
    <Grid item xs={6} sm={3} sx={{ pr: 2 }}>
      <Typography variant="subtitle1" fontWeight={500}>
        Operator
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <OperatorSelect
          intentId={intent.id}
          value={selectedOperator}
          onChange={setSelectedOperator}
          disabled={isAssigning}
        />
        <Button
          variant="outlined"
          size="small"
          disabled={!selectedOperator || isAssigning}
          onClick={() => setIsConfirmOpen(true)}
        >
          Assign Operator
        </Button>
      </Box>
      <Dialog open={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Operator</DialogTitle>
        <DialogContent>
          <Typography>
            Assign this citation to <b>{selectedOperator?.organizationName}</b>? The operator will be billed for the
            session, and the assignment cannot be changed afterward.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button fullWidth onClick={() => setIsConfirmOpen(false)}>
            Cancel
          </Button>
          <LoadingButton fullWidth variant="contained" color="primary" loading={isAssigning} onClick={handleAssign}>
            Assign
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default OperatorAssignmentItem;
